import {ReactNode, useMemo} from 'react';

import {
  FileUploaderProvider as ConversationsUIFileUploaderProvider,
  FileUploaderValue,
} from '@stripe-internal/conversations-ui';

import {useApp, useConfig} from '../hooks/appHooks';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_FILE_NAME_LENGTH = 200;

const supportedMimeTypes: string[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'text/plain',
  'text/xml',
  'text/csv',
  'text/tab-separated-values',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

class ValidationError extends Error {
  file?: File;

  code?: string;

  constructor({
    message,
    file,
    code,
  }: {
    message: string;
    file?: File;
    code?: string;
  }) {
    super(message);
    this.file = file;
    this.code = code;
  }
}

const validateFile = (file: File) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new ValidationError({
      message: 'File is too large',
      file,
      code: 'file_too_large',
    });
  }

  if (!supportedMimeTypes.includes(file.type)) {
    throw new ValidationError({
      message: 'File type is not supported',
      file,
      code: 'upload_type_not_supported',
    });
  }

  if (file.name.length > MAX_FILE_NAME_LENGTH) {
    throw new ValidationError({
      message: 'File name is too long',
      file,
      code: 'file_name_too_long',
    });
  }
};

const uploadFile = async ({
  uploadsHost,
  apiKey,
  merchantId,
  livemode,
  file,
  abortSignal,
  purpose = 'support_attachment',
}: {
  uploadsHost: string;
  apiKey: string;
  merchantId: string;
  livemode?: boolean;
  file: File;
  abortSignal?: AbortSignal;
  purpose?: 'support_attachment' | 'universal_help_attachment';
}): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('purpose', purpose);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${apiKey}`,
    'Stripe-Account': merchantId,
  };

  if (livemode != null) {
    headers['Stripe-Livemode'] = livemode ? 'true' : 'false';
  }

  // See: https://stripe.com/docs/api/files
  // note we don't use clientFetch here. the clientFetch should only really
  // be used for request to conversations-srv endpoints. the calls may be
  // wrapped adding headers and/or re-writing the paths as appropriate.
  // for uploads, we are calling the files api directly.
  const response = await fetch(`${uploadsHost}/v1/files`, {
    method: 'POST',
    headers,
    body: formData,
    signal: abortSignal,
  });

  if (!response.ok) {
    const data = await response.json();
    const message = data?.error?.message || 'Failed to upload file';
    const code = data?.error?.message_code;
    throw new ValidationError({message, file, code});
  }

  const data = await response.json();
  return data.id;
};

type Props = {
  children: ReactNode;
};

export function FileUploadProvider({children}: Props) {
  const config = useConfig();
  const app = useApp();
  const {visitor, merchant, livemode} = app.profile;
  const {
    urls: {uploadsHost},
  } = config;
  const {analytics} = app;

  const fileUploaderValue: FileUploaderValue = useMemo(() => {
    if (!visitor.sessionApiKey || !merchant?.id) {
      return {available: false};
    }

    return {
      available: true,
      upload: async (
        file: File,
        {abortSignal}: {abortSignal?: AbortSignal},
        purpose?: 'support_attachment' | 'universal_help_attachment',
      ) => {
        try {
          const fileId = await uploadFile({
            uploadsHost,
            apiKey: visitor.sessionApiKey as string,
            merchantId: merchant.id,
            livemode,
            file,
            abortSignal,
            purpose,
          });

          analytics.action('support_conversations_attachments_upload_success', {
            mimeType: file.type,
          });

          return fileId;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          analytics.action('support_conversations_attachments_upload_error', {
            errorCode: error?.code || '',
            mimeType: error?.file?.type || '',
          });
          throw error;
        }
      },
      validateFile,
    };
  }, [visitor.sessionApiKey, merchant, uploadsHost, livemode, analytics]);

  return (
    <ConversationsUIFileUploaderProvider value={fileUploaderValue}>
      {children}
    </ConversationsUIFileUploaderProvider>
  );
}
